<!-- src/components/DonateCard.vue -->

<template>
    <section class="donate-page" aria-labelledby="donate-title">
        <div class="donate-wrap">

            <!-- SUCCESS -->
            <div v-if="paymentSuccessful" class="success-card">
                <div class="success-mark">
                    <div class="success-icon">
                        ✓
                    </div>
                </div>

                <p class="eyebrow">
                    M.C Bridge of Hope
                </p>

                <h1 id="donate-title">
                    Thank you for making a difference.
                </h1>

                <p class="success-text">
                    Your donation has been received successfully.
                    Your generosity helps us continue creating hope,
                    connection and opportunity within our community.
                </p>

                <div class="success-banner">
                    <span class="success-dot"></span>

                    <div>
                        <small>
                            Donation status
                        </small>

                        <strong>
                            Payment successful
                        </strong>
                    </div>
                </div>

                <p class="receipt-note">
                    A donation receipt will be sent to the email address
                    provided during Stripe Checkout.
                </p>

                <div class="success-actions">
                    <button type="button" class="return-button" @click="returnToDonate">
                        Make another donation
                    </button>

                    <RouterLink to="/" class="home-link">
                        Return home
                    </RouterLink>
                </div>
            </div>


            <!-- DONATION PAGE -->
            <template v-else>

                <!-- LEFT -->
                <aside class="donate-story">
                    <div class="poster-glow poster-glow-one"></div>
                    <div class="poster-glow poster-glow-two"></div>

                    <div class="story-top">
                        <div class="brand">

                            <div>
                                <strong>
                                    M.C Bridge of Hope
                                </strong>

                                <small>
                                    Giving hope. Creating opportunity.
                                </small>
                            </div>
                        </div>

                        <span class="giving-badge">
                            Community giving
                        </span>
                    </div>


                    <div class="story-main">
                        <p class="eyebrow light">
                            Together we can make a difference
                        </p>

                        <h1 id="donate-title">
                            A little kindness can create
                            <span>
                                lasting hope.
                            </span>
                        </h1>

                        <p class="intro-text">
                            Your support helps us reach people,
                            strengthen communities and create opportunities
                            that can make a meaningful difference.
                        </p>

                        <div class="kindness-message">
                            <span>
                                “
                            </span>

                            <p>
                                Every donation, big or small, helps us
                                continue building bridges of hope.
                            </p>
                        </div>
                    </div>


                    <div class="impact-grid">
                        <div v-for="item in impactItems" :key="item.title" class="impact-item">
                            <div class="impact-number">
                                {{ item.number }}
                            </div>

                            <div>
                                <strong>
                                    {{ item.title }}
                                </strong>

                                <small>
                                    {{ item.text }}
                                </small>
                            </div>
                        </div>
                    </div>


                    <div class="story-footer">
                        <div class="secure-dot"></div>

                        <div>
                            <strong>
                                Safe & secure giving
                            </strong>

                            <small>
                                Payments are securely processed by Stripe.
                            </small>
                        </div>
                    </div>
                </aside>


                <!-- RIGHT -->
                <div class="donation-panel">
                    <header class="panel-header">
                        <p class="eyebrow">
                            Make a donation
                        </p>

                        <h2>
                            Choose an amount
                        </h2>

                        <p>
                            Every contribution matters.
                            Choose an amount that feels right for you.
                        </p>
                    </header>


                    <div class="selected-amount">
                        <div>
                            <span>
                                You're giving
                            </span>

                            <strong>
                                ${{ donationAmount }}
                            </strong>
                        </div>

                        <span class="aud">
                            AUD
                        </span>
                    </div>


                    <div class="preset-grid">
                        <button v-for="preset in presetAmounts" :key="preset" type="button" :class="{
                            active:
                                donationAmount === preset &&
                                customAmount === ''
                        }" @click="selectPreset(preset)">
                            ${{ preset }}
                        </button>
                    </div>


                    <label class="field">
                        <span>
                            Or enter another amount
                        </span>

                        <div class="amount-input">
                            <span class="currency">
                                $
                            </span>

                            <input v-model="customAmount" type="number" min="20" max="5000" step="1"
                                placeholder="Enter amount" @input="setCustomAmount" />

                            <small>
                                AUD
                            </small>
                        </div>
                    </label>


                    <div class="message-card">
                        <label class="field message-field">
                            <div class="message-label">
                                <div>
                                    <span>
                                        Leave a message
                                    </span>

                                    <small>
                                        Optional
                                    </small>
                                </div>

                                <span class="heart">
                                    ♡
                                </span>
                            </div>

                            <textarea v-model.trim="donationMessage" maxlength="450" rows="4"
                                placeholder="Share a kind message with your donation..."></textarea>

                            <small class="character-count">
                                {{ donationMessage.length }}/450
                            </small>
                        </label>
                    </div>


                    <div class="donation-summary">
                        <div>
                            <span>
                                Donation
                            </span>

                            <strong>
                                ${{ donationAmount }} AUD
                            </strong>
                        </div>

                        <div>
                            <span>
                                Frequency
                            </span>

                            <strong>
                                One-off
                            </strong>
                        </div>
                    </div>


                    <p v-if="errorMessage" class="error-message" role="alert">
                        {{ errorMessage }}
                    </p>


                    <button type="button" class="checkout-button" :disabled="loading" @click="startCheckout">
                        <span v-if="loading">
                            Taking you to secure payment...
                        </span>

                        <template v-else>
                            Donate ${{ donationAmount }}
                            <span>
                                →
                            </span>
                        </template>
                    </button>


                    <div class="secure-checkout">
                        <span>
                            🔒
                        </span>

                        <p>
                            Secure checkout powered by Stripe.
                            Your payment information is never stored by us.
                        </p>
                    </div>


                    <div class="recurring">
                        <div>
                            <strong>
                                Want to give regularly?
                            </strong>

                            <p>
                                We would love to hear from you about
                                weekly, fortnightly or monthly giving.
                            </p>
                        </div>

                        <RouterLink to="/contact">
                            Get in touch →
                        </RouterLink>
                    </div>
                </div>

            </template>
        </div>
    </section>
</template>


<script>
import axios from "axios";
import { useHead } from "@unhead/vue";

import logoSrc from "@/assets/logo7.png";
import SeoData from "./SeoData.js";

export default {
    name: "DonateCard",

    setup() {
        useHead(SeoData.donate);

        return {};
    },

    data() {
        return {
            logoSrc,

            donationAmount: 50,
            customAmount: "",
            donationMessage: "",

            loading: false,
            errorMessage: "",

            paymentSuccessful: false,
            sessionId: "",

            presetAmounts: [
                20,
                50,
                100,
                250,
                500,
            ],

            impactItems: [
                {
                    number: "01",
                    title: "Care",
                    text: "Supporting people and practical community needs.",
                },

                {
                    number: "02",
                    title: "Connection",
                    text: "Helping us reach and bring people together.",
                },

                {
                    number: "03",
                    title: "Opportunity",
                    text: "Creating pathways toward brighter futures.",
                },
            ],
        };
    },

    mounted() {
        this.checkPaymentReturn();
    },

    watch: {
        "$route.fullPath"() {
            this.checkPaymentReturn();
        },
    },

    methods: {
        checkPaymentReturn() {
            const sessionId =
                this.$route?.query?.session_id || "";

            if (
                this.$route.path === "/donate/success" &&
                sessionId &&
                String(sessionId).startsWith("cs_")
            ) {
                this.sessionId =
                    String(sessionId);

                this.paymentSuccessful =
                    true;

                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });

                return;
            }

            this.sessionId = "";
            this.paymentSuccessful = false;
        },

        returnToDonate() {
            this.sessionId = "";
            this.paymentSuccessful = false;
            this.donationAmount = 50;
            this.customAmount = "";
            this.donationMessage = "";
            this.errorMessage = "";

            this.$router.replace("/donate");
        },

        selectPreset(amount) {
            this.donationAmount = amount;
            this.customAmount = "";
            this.errorMessage = "";
        },

        setCustomAmount() {
            this.errorMessage = "";

            if (this.customAmount === "") {
                return;
            }

            const amount =
                Number(this.customAmount);

            if (Number.isFinite(amount)) {
                this.donationAmount =
                    Math.round(amount);
            }
        },

        async startCheckout() {
            this.errorMessage = "";

            if (this.donationAmount < 20) {
                this.errorMessage =
                    "Minimum donation is $20.";

                return;
            }

            if (this.donationAmount > 5000) {
                this.errorMessage =
                    "Maximum donation is $5000.";

                return;
            }

            this.loading = true;

            try {
                const { data } =
                    await axios.post(
                        // "http://localhost:3000/marychan/donate",
                        "https://my-app-two-1d26224db3b5.herokuapp.com/marychan/donate",

                        {
                            amountAud:
                                this.donationAmount,

                            amount:
                                this.donationAmount,

                            currency:
                                "aud",

                            frequency:
                                "one_off",

                            donorName:
                                "",

                            donorEmail:
                                "",

                            message:
                                this.donationMessage,

                            donorMessage:
                                this.donationMessage,

                            campaignName:
                                "M.C Bridge of Hope",

                            campaignId:
                                "mary-chan-bridge-of-hope",
                        },
                    );

                if (
                    !data?.success ||
                    !data?.url
                ) {
                    throw new Error(
                        data?.error ||
                        "Could not start donation checkout.",
                    );
                }

                window.location.assign(
                    data.url,
                );
            } catch (error) {
                this.errorMessage =
                    error.response?.data?.error ||
                    error.message ||
                    "Something went wrong. Please try again.";
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>


<style scoped lang="scss">
.donate-page {
    --navy: #07384d;
    --navy-deep: #042b3c;
    --navy-soft: #0d526d;
    --gold: #c5a36d;
    --gold-light: #e4cfaa;
    --cream: #f8f1e5;
    --cream-soft: #fffaf2;
    --paper: #ffffff;
    --text: #183039;
    --muted: #728086;
    --border: rgba(7, 56, 77, 0.11);

    min-height: 75vh;
    padding: 28px 13px;

    color: var(--text);

    background:
        radial-gradient(circle at 15% 5%,
            rgba(197, 163, 109, 0.12),
            transparent 28%),
        linear-gradient(180deg,
            #fffdf9,
            #f8f4ed);

    @media (min-width: 760px) {
        padding: 46px 20px;
    }

    @media (min-width: 1200px) {
        padding: 72px 24px;
    }


    .donate-wrap {
        display: grid;

        width: min(1180px, 100%);
        margin: 0 auto;

        gap: 18px;

        @media (min-width: 760px) {
            gap: 22px;
        }

        @media (min-width: 1200px) {
            grid-template-columns:
                minmax(0, 1fr) 470px;
        }
    }


    .eyebrow {
        margin: 0 0 10px;

        color: var(--gold);

        font-size: 10px;
        font-weight: 900;

        letter-spacing: 0.18em;
        text-transform: uppercase;

        &.light {
            color: var(--gold-light);
        }
    }


    .donate-story {
        position: relative;

        display: flex;
        flex-direction: column;

        overflow: hidden;

        padding: 24px;

        border-radius: 23px;

        color: #ffffff;

        background:
            linear-gradient(145deg,
                var(--navy-soft),
                var(--navy-deep));

        box-shadow:
            0 30px 75px rgba(4, 43, 60, 0.18);

        @media (min-width: 760px) {
            padding: 34px;
            border-radius: 28px;
        }

        @media (min-width: 1200px) {
            min-height: 680px;
            padding: 40px;
            border-radius: 34px;
        }
    }


    .poster-glow {
        position: absolute;

        border-radius: 50%;

        pointer-events: none;

        &.poster-glow-one {
            top: -110px;
            right: -100px;

            width: 280px;
            height: 280px;

            background:
                rgba(197, 163, 109, 0.17);

            @media (min-width: 760px) {
                top: -130px;
                right: -110px;

                width: 330px;
                height: 330px;
            }

            @media (min-width: 1200px) {
                top: -150px;
                right: -120px;

                width: 390px;
                height: 390px;
            }
        }

        &.poster-glow-two {
            bottom: -120px;
            left: -100px;

            width: 270px;
            height: 270px;

            background:
                rgba(255, 255, 255, 0.045);

            @media (min-width: 760px) {
                bottom: -150px;
                left: -110px;

                width: 320px;
                height: 320px;
            }

            @media (min-width: 1200px) {
                bottom: -170px;
                left: -120px;

                width: 360px;
                height: 360px;
            }
        }
    }


    .story-top {
        position: relative;
        z-index: 2;

        display: flex;
        justify-content: space-between;
        align-items: flex-start;

        gap: 16px;

        @media (min-width: 760px) {
            align-items: center;
        }
    }


    .brand {
        display: flex;
        align-items: center;

        gap: 11px;

        .logo {
            width: 52px;
            height: 52px;

            padding: 6px;

            border-radius: 15px;

            object-fit: contain;

            background: #ffffff;

            @media (min-width: 760px) {
                width: 58px;
                height: 58px;

                border-radius: 17px;
            }
        }

        strong,
        small {
            display: block;
        }

        strong {
            font-size: 12px;

            @media (min-width: 760px) {
                font-size: 13px;
            }
        }

        small {
            margin-top: 3px;

            color:
                rgba(255, 255, 255, 0.55);

            font-size: 8px;

            @media (min-width: 760px) {
                font-size: 9px;
            }
        }
    }


    .giving-badge {
        display: none;

        padding: 8px 12px;

        border:
            1px solid rgba(255, 255, 255, 0.12);

        border-radius: 999px;

        color:
            rgba(255, 255, 255, 0.75);

        background:
            rgba(255, 255, 255, 0.06);

        font-size: 9px;
        font-weight: 700;

        @media (min-width: 760px) {
            display: inline-block;
        }
    }


    .story-main {
        position: relative;
        z-index: 2;

        padding: 45px 0 30px;

        h1 {
            max-width: 690px;

            margin: 0;

            font-size:
                clamp(40px, 10vw, 52px);

            line-height: 1;

            letter-spacing: -0.04em;

            span {
                color: var(--gold-light);
            }
        }

        @media (min-width: 760px) {
            padding: 55px 0 35px;

            h1 {
                font-size:
                    clamp(48px, 7vw, 64px);
            }
        }

        @media (min-width: 1200px) {
            margin: auto 0;
            padding: 60px 0 38px;

            h1 {
                font-size:
                    clamp(52px, 5vw, 70px);
            }
        }
    }


    .intro-text {
        max-width: 620px;

        margin: 18px 0 0;

        color:
            rgba(255, 255, 255, 0.72);

        font-size: 13px;
        line-height: 1.7;

        @media (min-width: 760px) {
            margin-top: 22px;

            font-size: 15px;
            line-height: 1.75;
        }
    }


    .kindness-message {
        display: flex;

        max-width: 500px;

        gap: 10px;

        margin-top: 22px;

        padding: 14px 15px;

        border-left:
            3px solid var(--gold);

        border-radius:
            0 14px 14px 0;

        background:
            rgba(255, 255, 255, 0.055);

        >span {
            color: var(--gold-light);

            font-size: 28px;
            line-height: 1;
        }

        p {
            margin: 0;

            color:
                rgba(255, 255, 255, 0.78);

            font-size: 11px;
            line-height: 1.6;
        }

        @media (min-width: 760px) {
            margin-top: 25px;

            padding: 16px 18px;

            >span {
                font-size: 30px;
            }

            p {
                font-size: 12px;
            }
        }
    }


    .impact-grid {
        position: relative;
        z-index: 2;

        display: grid;
        grid-template-columns: 1fr;

        gap: 9px;

        @media (min-width: 760px) {
            grid-template-columns:
                repeat(3, 1fr);
        }
    }


    .impact-item {
        padding: 14px;

        border:
            1px solid rgba(255, 255, 255, 0.11);

        border-radius: 15px;

        background:
            rgba(255, 255, 255, 0.055);

        .impact-number {
            display: grid;
            place-items: center;

            width: 31px;
            height: 31px;

            margin-bottom: 10px;

            border-radius: 9px;

            color: var(--navy-deep);
            background: var(--gold-light);

            font-size: 9px;
            font-weight: 900;
        }

        strong,
        small {
            display: block;
        }

        strong {
            font-size: 12px;
        }

        small {
            margin-top: 4px;

            color:
                rgba(255, 255, 255, 0.5);

            font-size: 9px;
            line-height: 1.45;
        }
    }


    .story-footer {
        position: relative;
        z-index: 2;

        display: flex;
        align-items: center;

        gap: 10px;

        margin-top: 24px;
        padding-top: 18px;

        border-top:
            1px solid rgba(255, 255, 255, 0.1);

        .secure-dot {
            width: 8px;
            height: 8px;

            flex: 0 0 auto;

            border-radius: 50%;

            background: var(--gold);
        }

        strong,
        small {
            display: block;
        }

        strong {
            font-size: 10px;
        }

        small {
            margin-top: 2px;

            color:
                rgba(255, 255, 255, 0.45);

            font-size: 9px;
        }
    }


    .donation-panel {
        padding: 24px;

        border:
            1px solid var(--border);

        border-radius: 23px;

        background:
            rgba(255, 255, 255, 0.96);

        box-shadow:
            0 30px 75px rgba(4, 43, 60, 0.1);

        @media (min-width: 760px) {
            padding: 28px;
            border-radius: 28px;
        }

        @media (min-width: 1200px) {
            padding: 31px;
            border-radius: 34px;
        }
    }


    .panel-header {
        h2 {
            margin: 0;

            color: var(--navy);

            font-size:
                clamp(31px, 8vw, 38px);

            line-height: 1;

            letter-spacing: -0.04em;

            @media (min-width: 760px) {
                font-size:
                    clamp(34px, 5vw, 42px);
            }
        }

        >p:not(.eyebrow) {
            margin: 10px 0 0;

            color: var(--muted);

            font-size: 12px;
            line-height: 1.6;
        }
    }


    .selected-amount {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;

        margin: 23px 0 17px;
        padding: 18px;

        border:
            1px solid rgba(197, 163, 109, 0.18);

        border-radius: 18px;

        background:
            linear-gradient(140deg,
                var(--cream),
                var(--cream-soft));

        >div {

            span,
            strong {
                display: block;
            }

            span {
                margin-bottom: 5px;

                color: var(--muted);

                font-size: 9px;
                font-weight: 800;

                letter-spacing: 0.08em;
                text-transform: uppercase;
            }

            strong {
                color: var(--navy);

                font-size: 44px;
                line-height: 1;

                letter-spacing: -0.05em;

                @media (min-width: 760px) {
                    font-size: 48px;
                }
            }
        }

        .aud {
            padding-bottom: 5px;

            color: var(--muted);

            font-size: 9px;
            font-weight: 800;
        }

        @media (min-width: 760px) {
            padding: 19px 20px;
        }
    }


    .preset-grid {
        display: grid;

        grid-template-columns:
            repeat(3, 1fr);

        gap: 7px;

        margin-bottom: 18px;

        button {
            min-height: 44px;

            border:
                1px solid var(--border);

            border-radius: 12px;

            color: var(--navy);
            background: #ffffff;

            cursor: pointer;

            font-family: inherit;
            font-size: 12px;
            font-weight: 800;

            transition:
                transform 0.18s ease,
                background 0.18s ease;

            &:hover {
                transform:
                    translateY(-1px);

                background:
                    var(--cream-soft);
            }

            &.active {
                color: #ffffff;

                background:
                    var(--navy);

                border-color:
                    var(--navy);
            }
        }

        @media (min-width: 760px) {
            grid-template-columns:
                repeat(5, 1fr);
        }
    }


    .field {
        display: block;

        margin-bottom: 18px;

        >span {
            display: block;

            margin-bottom: 7px;

            color: var(--muted);

            font-size: 9px;
            font-weight: 800;

            letter-spacing: 0.05em;
            text-transform: uppercase;
        }

        textarea {
            width: 100%;
            min-height: 105px;

            box-sizing: border-box;

            padding: 14px;

            resize: vertical;

            border:
                1px solid var(--border);

            border-radius: 13px;

            outline: none;

            color: var(--text);
            background: #ffffff;

            font: inherit;
            font-size: 12px;
            line-height: 1.5;

            &:focus {
                border-color:
                    var(--gold);

                box-shadow:
                    0 0 0 3px rgba(197, 163, 109, 0.1);
            }
        }
    }


    .amount-input {
        display: grid;

        grid-template-columns:
            42px 1fr auto;

        align-items: center;

        overflow: hidden;

        border:
            1px solid var(--border);

        border-radius: 13px;

        background: #ffffff;

        .currency {
            display: grid;
            place-items: center;

            height: 49px;

            border-right:
                1px solid var(--border);

            color: var(--navy);

            font-weight: 900;
        }

        input {
            width: 100%;
            height: 49px;

            box-sizing: border-box;

            padding: 0 12px;

            border: 0;
            outline: none;

            font: inherit;
            font-weight: 700;
        }

        small {
            padding-right: 13px;

            color: var(--muted);

            font-size: 9px;
            font-weight: 800;
        }
    }


    .message-card {
        margin-bottom: 18px;
        padding: 14px;

        border-radius: 17px;

        background:
            #faf8f4;
    }


    .message-field {
        margin: 0;
    }


    .message-label {
        display: flex;
        justify-content: space-between;
        align-items: center;

        margin-bottom: 8px;

        >div {

            span,
            small {
                display: block;
            }

            span {
                color: var(--navy);

                font-size: 10px;
                font-weight: 900;
            }

            small {
                margin-top: 2px;

                color: var(--muted);

                font-size: 8px;
            }
        }

        .heart {
            color: var(--gold);

            font-size: 21px;
        }
    }


    .character-count {
        display: block;

        margin-top: 5px;

        color: var(--muted);

        font-size: 8px;
        text-align: right;
    }


    .donation-summary {
        display: grid;
        grid-template-columns: 1fr;

        gap: 8px;

        margin-bottom: 17px;

        >div {
            padding: 12px;

            border:
                1px solid var(--border);

            border-radius: 12px;

            background: #ffffff;

            span,
            strong {
                display: block;
            }

            span {
                margin-bottom: 3px;

                color: var(--muted);

                font-size: 8px;
                text-transform: uppercase;
            }

            strong {
                color: var(--navy);

                font-size: 11px;
            }
        }

        @media (min-width: 760px) {
            grid-template-columns:
                1fr 1fr;
        }
    }


    .checkout-button {
        display: flex;
        justify-content: center;
        align-items: center;

        gap: 9px;

        width: 100%;
        min-height: 55px;

        border: 0;
        border-radius: 999px;

        color: #ffffff;

        background:
            linear-gradient(135deg,
                var(--navy-soft),
                var(--navy-deep));

        cursor: pointer;

        font-family: inherit;
        font-size: 13px;
        font-weight: 900;

        box-shadow:
            0 12px 28px rgba(4, 43, 60, 0.18);

        &:disabled {
            cursor: wait;
            opacity: 0.65;
        }
    }


    .secure-checkout {
        display: flex;
        justify-content: center;
        align-items: flex-start;

        gap: 6px;

        margin-top: 10px;

        >span {
            font-size: 9px;
        }

        p {
            max-width: 300px;

            margin: 0;

            color: var(--muted);

            font-size: 8px;
            line-height: 1.45;

            text-align: center;
        }
    }


    .recurring {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        gap: 12px;

        margin-top: 21px;
        padding-top: 17px;

        border-top:
            1px solid var(--border);

        strong {
            color: var(--navy);

            font-size: 10px;
        }

        p {
            max-width: 250px;

            margin: 4px 0 0;

            color: var(--muted);

            font-size: 9px;
            line-height: 1.5;
        }

        a {
            flex-shrink: 0;

            color: var(--navy);

            font-size: 9px;
            font-weight: 900;

            text-decoration: none;
        }

        @media (min-width: 760px) {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;

            gap: 14px;
        }
    }


    .error-message {
        margin: 0 0 13px;
        padding: 10px 12px;

        border-radius: 11px;

        color: #991b1b;
        background: #fff4f4;

        font-size: 10px;
    }


    .success-card {
        grid-column: 1 / -1;

        width: min(680px, 100%);
        margin: 10px auto;

        box-sizing: border-box;

        padding: 25px;

        border:
            1px solid var(--border);

        border-radius: 23px;

        background:
            linear-gradient(180deg,
                #ffffff,
                #fffaf2);

        text-align: center;

        box-shadow:
            0 30px 75px rgba(4, 43, 60, 0.11);

        h1 {
            margin: 0;

            color: var(--navy);

            font-size:
                clamp(36px, 10vw, 46px);

            line-height: 1.05;

            letter-spacing: -0.045em;
        }

        @media (min-width: 760px) {
            margin: 20px auto;

            padding: 42px 36px;

            border-radius: 28px;

            h1 {
                font-size:
                    clamp(42px, 6vw, 52px);
            }
        }

        @media (min-width: 1200px) {
            margin: 30px auto;

            padding: 58px 45px;

            border-radius: 32px;

            h1 {
                font-size:
                    clamp(46px, 5vw, 55px);
            }
        }
    }


    .success-mark {
        display: grid;
        place-items: center;

        width: 88px;
        height: 88px;

        margin: 0 auto 20px;

        border-radius: 50%;

        background:
            rgba(197, 163, 109, 0.12);

        @media (min-width: 760px) {
            width: 100px;
            height: 100px;
        }
    }


    .success-icon {
        display: grid;
        place-items: center;

        width: 64px;
        height: 64px;

        border-radius: 50%;

        color: #ffffff;

        background:
            linear-gradient(135deg,
                var(--navy-soft),
                var(--navy-deep));

        font-size: 28px;
        font-weight: 900;

        @media (min-width: 760px) {
            width: 72px;
            height: 72px;

            font-size: 31px;
        }
    }


    .success-text {
        max-width: 520px;

        margin: 18px auto 26px;

        color: var(--muted);

        font-size: 13px;
        line-height: 1.7;

        @media (min-width: 760px) {
            font-size: 14px;
            line-height: 1.75;
        }
    }


    .success-banner {
        display: flex;
        align-items: center;

        width: fit-content;

        gap: 10px;

        margin: 0 auto 20px;
        padding: 12px 18px;

        border-radius: 13px;

        background:
            var(--cream);

        text-align: left;

        .success-dot {
            width: 9px;
            height: 9px;

            border-radius: 50%;

            background:
                var(--navy-soft);
        }

        small,
        strong {
            display: block;
        }

        small {
            color: var(--muted);

            font-size: 8px;
            text-transform: uppercase;
        }

        strong {
            margin-top: 2px;

            color: var(--navy);

            font-size: 11px;
        }
    }


    .receipt-note {
        max-width: 440px;

        margin: 0 auto 25px;

        color: var(--muted);

        font-size: 10px;
        line-height: 1.6;
    }


    .success-actions {
        display: flex;
        justify-content: center;
        align-items: center;

        gap: 13px;

        flex-wrap: wrap;
    }


    .return-button {
        min-height: 51px;

        padding: 0 27px;

        border: 0;
        border-radius: 999px;

        color: #ffffff;

        background:
            var(--navy);

        cursor: pointer;

        font-family: inherit;
        font-size: 11px;
        font-weight: 900;
    }


    .home-link {
        padding: 15px 20px;

        color: var(--navy);

        font-size: 10px;
        font-weight: 800;

        text-decoration: none;
    }
}
</style>